import { ElementType, ReactElement, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { useBeaconRouter } from '../../route/route.hooks';
import { useNavToLastKnownLocation } from '../../store/nav-history/nav-history.hooks';
import { useQueryParameters } from '../../util/router';
import { CheckingLogin } from './confirm/checking-login';
import { FailedLogin } from './confirm/failed-login';
import { ValidLinkLogin } from './confirm/valid-link-login';
import { WaitingLogin } from './confirm/waiting-login';
import { LoginWrapper } from './login-wrapper';
import './login.css';
import {
  MagicLink,
  useConsumeAuthChallengeLazy,
  useMagicLink,
} from './login.hooks';

type ConfirmState = 'waiting' | 'validLink' | 'checking' | 'failed';

export interface LoginConfirmProps {
  email?: string | null;
  expiresIn?: number;
  loading: boolean;
  consumeChallengeOnClick: () => void;
  startOverClick: () => void;
  magicLink: MagicLink;
}

const LoginConfirm = (): ReactElement => {
  const [confirmState, setConfirmState] = useState<ConfirmState>('waiting');

  const magicLink = useMagicLink();
  const { navToLastKnownTenantLocation } = useNavToLastKnownLocation();

  const [consumeAuthChallenge, { loading, workspaces }] =
    useConsumeAuthChallengeLazy();

  const { builder } = useBeaconRouter();

  const queryParams = useQueryParameters();
  const email = queryParams.get('email');
  const username = queryParams.get('username');
  const expiresIn = Number(queryParams.get('expiresIn')) || 0;

  const { push } = useHistory();
  useEffect(() => {
    if (!loading && workspaces) {
      if (workspaces.length > 0) {
        const requestedWorkspace = workspaces.find(
          (w) => w.user.username === username
        );
        const tenantId = workspaces[0].tenant.id;
        navToLastKnownTenantLocation(requestedWorkspace?.tenant.id ?? tenantId);
      } else {
        setConfirmState('failed');
      }
    }
  }, [loading, workspaces, push, navToLastKnownTenantLocation, username]);

  useEffect(() => {
    if (magicLink.isValid()) {
      setConfirmState('validLink');
    }
  }, [magicLink]);

  const consumeChallengeOnClick = () => {
    setConfirmState('checking');
    consumeAuthChallenge(magicLink);
  };

  const startOverClick = () => {
    push({
      pathname: builder.getAuthLoginRoute(),
      search: `email=${encodeURIComponent(email || '')}`,
    });
  };

  const sharedProps: LoginConfirmProps = {
    email,
    expiresIn,
    consumeChallengeOnClick,
    loading,
    magicLink,
    startOverClick,
  };

  const LoginStateComponent = getComponentForState(confirmState);

  return (
    <LoginWrapper>
      <LoginStateComponent {...sharedProps} />
    </LoginWrapper>
  );
};

const getComponentForState = (
  state: ConfirmState
): ElementType<LoginConfirmProps> => {
  switch (state) {
    case 'checking':
      return CheckingLogin;
    case 'validLink':
      return ValidLinkLogin;
    case 'failed':
      return FailedLogin;
    case 'waiting':
    default:
      return WaitingLogin;
  }
};

export { LoginConfirm };
